import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Loader2,
  Eye,
  FileText,
  Pencil,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Info,
} from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../api/api";
import MainLayout from "../sidebar/Sidebar.js";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";

export default function EditProposal() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { proposalId } = useParams();
  const [location, setLocation] = useState("Proposals");

  const [editingField, setEditingField] = useState(null);
  const [editingOriginalValue, setEditingOriginalValue] = useState(null); // Track value when editing started
  const [isSaving, setIsSaving] = useState(false);
  const [proposalTitle, setProposalTitle] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Local state for display options (for preview only, not saved per-project)
  const [displayOptions, setDisplayOptions] = useState({
    show_quantities: true,
    show_line_item_prices: true,
    show_total: true,
  });

  // Local state for client display options
  const [clientDisplayOptions, setClientDisplayOptions] = useState({
    show_client_name: true,
    show_client_email: true,
    show_client_phone: true,
  });

  // Local state for logo position (for preview only, not saved per-project)
  const [logoPosition, setLogoPosition] = useState("left");

  // Local state for editable fields (merged values)
  const [localOverrides, setLocalOverrides] = useState({
    header_text: "",
    contract_terms: "",
    footer_text: "",
  });

  // Fetch existing config
  const { data: globalConfig, isLoading: isLoadingConfig } = useQuery({
    queryKey: ["proposalConfig"],
    queryFn: async () => {
      try {
        const token = await import("../firebase").then(m => m.auth.currentUser?.getIdToken());
        const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
        const response = await fetch(`${baseUrl}/proposals/config`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 404) {
          return null;
        }

        if (!response.ok) {
          throw new Error("Failed to fetch config");
        }

        return response.json();
      } catch (error) {
        if (error.message !== "Failed to fetch config") {
          console.error("Error fetching proposal config:", error);
        }
        return null;
      }
    },
    retry: false,
  });

  // Fetch projects for the dropdown
  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      const response = await api.get("/projects/");
      return response.data;
    },
  });

  // Fetch existing proposals
  const { data: proposalsData, isLoading: isLoadingProposals } = useQuery({
    queryKey: ["proposals"],
    queryFn: async () => {
      const response = await api.get("/proposals/");
      return response.data;
    },
  });

  const proposals = proposalsData?.items || [];

  // Get the selected proposal object
  const selectedProposal = proposals.find(p => p.id === proposalId);
  const selectedProjectId = selectedProposal?.project_id || "";

  // Get the effective config for preview
  // When editing an existing proposal, use its config_snapshot to match the generated PDF
  const effectiveConfig = useMemo(() => {
    if (selectedProposal && selectedProposal.config_snapshot) {
      return selectedProposal.config_snapshot;
    }
    return globalConfig;
  }, [selectedProposal, globalConfig]);

  // Set proposal title when proposal is loaded
  useEffect(() => {
    if (selectedProposal) {
      setProposalTitle(selectedProposal.title || "");
    }
  }, [selectedProposal]);

  // Fetch project-specific overrides when project is selected
  const { data: projectOverrides, isLoading: isLoadingOverrides } = useQuery({
    queryKey: ["projectOverrides", selectedProjectId],
    queryFn: async () => {
      if (!selectedProjectId) return null;
      try {
        const response = await api.get(`/proposals/project-overrides/${selectedProjectId}`);
        return response.data;
      } catch (error) {
        if (error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!selectedProjectId,
  });

  // Fetch line items for the selected project
  const { data: lineItems = [], isLoading: isLoadingLineItems } = useQuery({
    queryKey: ["lineItems", selectedProjectId],
    queryFn: async () => {
      if (!selectedProjectId) return [];
      const response = await api.get(`/projects/${selectedProjectId}/line-items/`);
      return response.data;
    },
    enabled: !!selectedProjectId,
  });

  // Fetch project details for display
  const selectedProject = projects.find(p => p.id === selectedProjectId);

  // Update local overrides when project overrides or effective config changes
  useEffect(() => {
    if (selectedProjectId && effectiveConfig) {
      setLocalOverrides({
        header_text: projectOverrides?.header_text ?? effectiveConfig.header_text ?? "",
        contract_terms: projectOverrides?.contract_terms ?? effectiveConfig.contract_terms ?? "",
        footer_text: projectOverrides?.footer_text ?? effectiveConfig.footer_text ?? "",
      });
    }
  }, [selectedProjectId, projectOverrides, effectiveConfig]);

  // Sync display options with effective config (uses config_snapshot for existing proposals)
  useEffect(() => {
    if (effectiveConfig) {
      setDisplayOptions({
        show_quantities: effectiveConfig.show_quantities ?? true,
        show_line_item_prices: effectiveConfig.show_line_item_prices ?? true,
        show_total: effectiveConfig.show_total ?? true,
      });
      setClientDisplayOptions({
        show_client_name: effectiveConfig.show_client_name ?? true,
        show_client_email: effectiveConfig.show_client_email ?? true,
        show_client_phone: effectiveConfig.show_client_phone ?? true,
      });
    }
  }, [effectiveConfig]);

  // Sync logo position with effective config (uses config_snapshot for existing proposals)
  useEffect(() => {
    if (effectiveConfig) {
      setLogoPosition(effectiveConfig.logo_position || "left");
    }
  }, [effectiveConfig]);

  // Save overrides mutation
  const saveOverridesMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post(`/proposals/project-overrides/${selectedProjectId}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["projectOverrides", selectedProjectId]);
      toast.success("Changes saved");
      setIsSaving(false);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to save changes");
      setIsSaving(false);
    },
  });

  // Undo changes for a specific field - reset to global default
  const handleUndoField = useCallback((field) => {
    const globalValue = effectiveConfig?.[field] || "";

    setLocalOverrides(prev => ({
      ...prev,
      [field]: globalValue,
    }));

    // Save null value to clear the override for this field
    setIsSaving(true);
    saveOverridesMutation.mutate({
      ...localOverrides,
      [field]: null,
    });
  }, [effectiveConfig, localOverrides, saveOverridesMutation]);

  // Generate proposal mutation
  const generateProposalMutation = useMutation({
    mutationFn: async ({ projectId, title, logoPosition: logoPos, displayOpts, clientOpts, leadId }) => {
      const response = await api.post(`/proposals/generate/${projectId}`, {
        title,
        expires_days: 30,
        logo_position: logoPos,
        show_quantities: displayOpts.show_quantities,
        show_line_item_prices: displayOpts.show_line_item_prices,
        show_total: displayOpts.show_total,
        lead_id: leadId || null,
        show_client_name: clientOpts?.show_client_name ?? true,
        show_client_email: clientOpts?.show_client_email ?? true,
        show_client_phone: clientOpts?.show_client_phone ?? true,
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["proposals"]);
      toast.success("Proposal generated successfully!");
      navigate("/proposals");
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to generate proposal");
    },
  });

  // Handle starting to edit a field - capture the original value
  const handleStartEdit = useCallback((field) => {
    setEditingField(field);
    setEditingOriginalValue(localOverrides[field]);
  }, [localOverrides]);

  // Handle field change - only update local state, no save
  const handleFieldChange = useCallback((field, value) => {
    setLocalOverrides(prev => ({ ...prev, [field]: value }));
  }, []);

  // Handle blur - save when user clicks out of the field
  const handleFieldBlur = useCallback((field) => {
    setEditingField(null);

    const currentValue = localOverrides[field];

    // Only save if value actually changed during this editing session
    if (currentValue !== editingOriginalValue) {
      setIsSaving(true);
      saveOverridesMutation.mutate({
        ...localOverrides,
        [field]: currentValue || null,
      });
    }

    // Clear the original value tracker
    setEditingOriginalValue(null);
  }, [editingOriginalValue, localOverrides, saveOverridesMutation]);

  // Preview proposal
  const handlePreview = useCallback(async (projectId) => {
    try {
      const response = await api.get(`/proposals/preview/${projectId}`, {
        responseType: "text",
      });
      const previewWindow = window.open("", "_blank");
      if (previewWindow) {
        previewWindow.document.write(response.data);
        previewWindow.document.close();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load preview");
    }
  }, []);

  const isLoading = isLoadingConfig || isLoadingProposals;

  if (isLoading) {
    return (
      <MainLayout location={location} setLocation={setLocation}>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </MainLayout>
    );
  }

  if (!selectedProposal) {
    return (
      <MainLayout location={location} setLocation={setLocation}>
        <div className="flex flex-col items-center justify-center h-full text-gray-500">
          <FileText className="w-12 h-12 text-gray-300 mb-3" />
          <p className="text-lg mb-2">Proposal Not Found</p>
          <p className="text-sm mb-4">The proposal you're looking for doesn't exist</p>
          <Button
            onClick={() => navigate("/proposals")}
            variant="outline"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Proposals
          </Button>
        </div>
      </MainLayout>
    );
  }

  if (!globalConfig) {
    return (
      <MainLayout location={location} setLocation={setLocation}>
        <div className="flex flex-col items-center justify-center h-full text-gray-500">
          <FileText className="w-12 h-12 text-gray-300 mb-3" />
          <p className="text-lg mb-2">Configure Settings First</p>
          <p className="text-sm mb-4">Please set up your proposal settings before editing proposals</p>
          <Button
            onClick={() => navigate("/proposals/create")}
            style={{ backgroundColor: "#006de2", color: "white" }}
          >
            Configure Settings
          </Button>
        </div>
      </MainLayout>
    );
  }

  const accentColor = effectiveConfig.accent_color || "#006de2";

  return (
    <MainLayout location={location} setLocation={setLocation}>
      <div className="flex flex-col h-full overflow-hidden" style={{ backgroundColor: 'rgba(0, 109, 226, 0)' }}>
        <div className="flex-1 overflow-hidden py-6 px-5 min-h-0">
          <div className="h-full flex flex-col overflow-hidden">
            {/* Back Button Row */}
            <div className="flex items-center gap-3 pb-3 flex-shrink-0">
              <Button
                variant="customStandard"
                size="customStandard"
                onClick={() => navigate("/proposals")}
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              {isSaving && (
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Saving...
                </span>
              )}
            </div>

            {/* Action Bar */}
            <div className="max-w-5xl mx-auto w-full flex-shrink-0">
              <div className="flex items-end justify-between gap-4 pb-4">
                {/* Left: Title + Project */}
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    value={proposalTitle}
                    onChange={(e) => setProposalTitle(e.target.value)}
                    className="w-full text-xl font-semibold bg-transparent border-b-2 border-transparent focus:border-blue-500 focus:outline-none pb-1 transition-colors"
                    style={{ color: "#1C1C1C" }}
                    placeholder="Enter proposal title..."
                  />
                  {selectedProject && (
                    <div className="text-xs text-gray-500 mt-1">
                      {selectedProject.name} ({selectedProject.project_code})
                    </div>
                  )}
                </div>
                {/* Right: Action Buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="customStandard"
                    size="customStandard"
                    onClick={() => handlePreview(selectedProjectId)}
                  >
                    <Eye className="w-4 h-4 mr-1.5" />
                    Full Preview
                  </Button>
                  <div className="relative group">
                    <Button
                      size="sm"
                      onClick={() => generateProposalMutation.mutate({ projectId: selectedProjectId, title: proposalTitle, logoPosition: logoPosition, displayOpts: displayOptions, clientOpts: clientDisplayOptions, leadId: selectedProposal?.lead_id || null })}
                      disabled={generateProposalMutation.isPending || !proposalTitle.trim()}
                      style={{ backgroundColor: "#006de2", color: "white" }}
                    >
                      {generateProposalMutation.isPending ? (
                        <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                      ) : (
                        <FileText className="w-4 h-4 mr-1.5" />
                      )}
                      Regenerate PDF
                    </Button>
                    <div className="absolute right-0 top-full mt-1.5 w-64 bg-gray-800 text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                      <Info className="w-3 h-3 inline mr-1" />
                      Generating a new PDF will create a new proposal. The original will remain unchanged.
                    </div>
                  </div>
                </div>
              </div>

              {/* Settings Strip */}
              <div
                className="rounded-xl mb-4 overflow-hidden transition-all"
                style={{ backgroundColor: '#fff', border: '1px solid #e8e8e8' }}
              >
                <button
                  onClick={() => setSettingsOpen(!settingsOpen)}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide hover:bg-gray-50 transition-colors"
                >
                  <span>Settings</span>
                  {settingsOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
                {settingsOpen && (
                  <div className="px-4 pb-3 flex items-start justify-between gap-8 border-t border-gray-100 pt-3">
                    {/* Logo Position */}
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-2">Logo Position</div>
                      <div className="flex gap-1">
                        {["left", "center", "right"].map((pos) => (
                          <button
                            key={pos}
                            onClick={() => setLogoPosition(pos)}
                            className={`px-3 py-1 text-xs rounded-full capitalize transition-colors ${
                              logoPosition === pos
                                ? "text-white"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                            style={logoPosition === pos ? { backgroundColor: accentColor } : {}}
                          >
                            {pos}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Display Options */}
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-2">Display Options</div>
                      <div className="flex flex-wrap gap-x-5 gap-y-1.5">
                        {[
                          { key: "show_quantities", label: "Quantities" },
                          { key: "show_line_item_prices", label: "Line Prices" },
                          { key: "show_total", label: "Total" },
                        ].map(({ key, label }) => (
                          <label key={key} className="flex items-center gap-1.5 cursor-pointer">
                            <div
                              className="relative w-7 h-4 rounded-full transition-colors cursor-pointer"
                              style={{ backgroundColor: displayOptions[key] ? accentColor : '#d1d5db' }}
                              onClick={(e) => {
                                e.preventDefault();
                                setDisplayOptions(prev => ({ ...prev, [key]: !prev[key] }));
                              }}
                            >
                              <div
                                className="absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform"
                                style={{ left: displayOptions[key] ? '14px' : '2px' }}
                              />
                            </div>
                            <span className="text-xs text-gray-600">{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    {/* Client Display Options */}
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-2">Client Info</div>
                      <div className="flex flex-wrap gap-x-5 gap-y-1.5">
                        {[
                          { key: "show_client_name", label: "Name" },
                          { key: "show_client_email", label: "Email" },
                          { key: "show_client_phone", label: "Phone" },
                        ].map(({ key, label }) => (
                          <label key={key} className="flex items-center gap-1.5 cursor-pointer">
                            <div
                              className="relative w-7 h-4 rounded-full transition-colors cursor-pointer"
                              style={{ backgroundColor: clientDisplayOptions[key] ? accentColor : '#d1d5db' }}
                              onClick={(e) => {
                                e.preventDefault();
                                setClientDisplayOptions(prev => ({ ...prev, [key]: !prev[key] }));
                              }}
                            >
                              <div
                                className="absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform"
                                style={{ left: clientDisplayOptions[key] ? '14px' : '2px' }}
                              />
                            </div>
                            <span className="text-xs text-gray-600">{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="max-w-5xl mx-auto">
                {isLoadingOverrides || isLoadingLineItems ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  </div>
                ) : (
                  /* Paper-like Document Preview */
                  <div
                    className="bg-white rounded-2xl overflow-hidden"
                    style={{
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.04)',
                      padding: '2rem 2.5rem',
                      fontFamily: 'Arial, sans-serif',
                    }}
                  >
                    {/* Company Header */}
                    <div className="mb-8">
                      {logoPosition === 'center' ? (
                        <div className="text-center pb-5" style={{ paddingLeft: '1.5rem', paddingRight: '1.5rem' }}>
                          {effectiveConfig.logo_s3_key && (
                            <div className="mb-3">
                              <div className="inline-block bg-gray-100 px-4 py-2 rounded text-xs text-gray-500">
                                [Company Logo]
                              </div>
                            </div>
                          )}
                          <div className="text-lg font-bold" style={{ color: accentColor }}>
                            {effectiveConfig.company_name || "Your Company"}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {effectiveConfig.company_address && <div>{effectiveConfig.company_address}</div>}
                            <div>
                              {effectiveConfig.company_phone && <span>{effectiveConfig.company_phone}</span>}
                              {effectiveConfig.company_phone && effectiveConfig.company_email && <span> | </span>}
                              {effectiveConfig.company_email && <span>{effectiveConfig.company_email}</span>}
                            </div>
                          </div>
                        </div>
                      ) : logoPosition === 'right' ? (
                        <div className="flex justify-between items-start pb-5" style={{ paddingLeft: '1.5rem', paddingRight: '1.5rem' }}>
                          <div>
                            <div className="text-lg font-bold" style={{ color: accentColor }}>
                              {effectiveConfig.company_name || "Your Company"}
                            </div>
                            <div className="text-xs text-gray-500 mt-1" style={{ lineHeight: '1.4' }}>
                              {effectiveConfig.company_phone && <div>{effectiveConfig.company_phone}</div>}
                              {effectiveConfig.company_email && <div>{effectiveConfig.company_email}</div>}
                              {effectiveConfig.company_address && <div>{effectiveConfig.company_address}</div>}
                            </div>
                          </div>
                          <div>
                            {effectiveConfig.logo_s3_key && (
                              <div className="inline-block bg-gray-100 px-4 py-2 rounded text-xs text-gray-500">
                                [Company Logo]
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-between items-start pb-5" style={{ paddingLeft: '1.5rem', paddingRight: '1.5rem' }}>
                          <div>
                            {effectiveConfig.logo_s3_key && (
                              <div className="inline-block bg-gray-100 px-4 py-2 rounded text-xs text-gray-500">
                                [Company Logo]
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold" style={{ color: accentColor }}>
                              {effectiveConfig.company_name || "Your Company"}
                            </div>
                            <div className="text-xs text-gray-500 mt-1" style={{ lineHeight: '1.4' }}>
                              {effectiveConfig.company_phone && <div>{effectiveConfig.company_phone}</div>}
                              {effectiveConfig.company_email && <div>{effectiveConfig.company_email}</div>}
                              {effectiveConfig.company_address && <div>{effectiveConfig.company_address}</div>}
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Full-width header line */}
                      <div
                        style={{
                          borderBottom: `2px solid ${accentColor}`,
                          marginLeft: '-2.5rem',
                          marginRight: '-2.5rem',
                        }}
                      />
                    </div>

                    {/* Content with horizontal padding */}
                    <div style={{ paddingLeft: '1.5rem', paddingRight: '1.5rem' }}>
                    {/* Proposal Title */}
                    <div className="text-left mb-8">
                      <h2 className="text-base font-bold text-gray-800 mb-2" style={{ letterSpacing: '0.08em' }}>PROPOSAL</h2>
                    </div>

                    {/* Client and Project Information Section - Two Column Layout */}
                    <div className="mb-8 flex justify-between">
                      {/* Client Information (LEFT) */}
                      <div className="text-left">
                        {effectiveConfig?.lead && (
                          <>
                            {clientDisplayOptions.show_client_name && effectiveConfig.lead.name && (
                              <div className="flex items-baseline mb-1.5">
                                <span className="text-sm mr-2.5" style={{ color: '#1C1C1C', width: '45px' }}>Client:</span>
                                <span className="font-medium" style={{ color: '#1C1C1C', fontSize: '11pt' }}>
                                  {effectiveConfig.lead.name}
                                </span>
                              </div>
                            )}
                            {clientDisplayOptions.show_client_email && effectiveConfig.lead.email && (
                              <div className="flex items-baseline mb-1.5">
                                <span className="text-sm mr-2.5" style={{ color: '#1C1C1C', width: '45px' }}>Email:</span>
                                <span className="font-medium" style={{ color: '#1C1C1C', fontSize: '11pt' }}>
                                  {effectiveConfig.lead.email}
                                </span>
                              </div>
                            )}
                            {clientDisplayOptions.show_client_phone && effectiveConfig.lead.phone && (
                              <div className="flex items-baseline mb-1.5">
                                <span className="text-sm mr-2.5" style={{ color: '#1C1C1C', width: '45px' }}>Phone:</span>
                                <span className="font-medium" style={{ color: '#1C1C1C', fontSize: '11pt' }}>
                                  {effectiveConfig.lead.phone}
                                </span>
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {/* Project Information (RIGHT) */}
                      <div style={{ textAlign: 'right' }}>
                        <div className="flex items-baseline justify-end mb-1.5">
                          <span className="text-sm mr-2.5" style={{ color: '#1C1C1C', width: '90px', textAlign: 'left' }}>Project Name:</span>
                          <span className="font-medium" style={{ color: '#1C1C1C', fontSize: '11pt', width: '160px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                            {selectedProject?.name || "-"}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-end mb-1.5">
                          <span className="text-sm mr-2.5" style={{ color: '#1C1C1C', width: '90px', textAlign: 'left' }}>Project Code:</span>
                          <span className="font-medium" style={{ color: '#1C1C1C', fontSize: '11pt', width: '160px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                            {selectedProject?.project_code || "-"}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-end">
                          <span className="text-sm mr-2.5" style={{ color: '#1C1C1C', width: '90px', textAlign: 'left' }}>Created Date:</span>
                          <span className="font-medium" style={{ color: '#1C1C1C', fontSize: '11pt', width: '160px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                            {selectedProject?.created_at
                              ? new Date(selectedProject.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: '2-digit' })
                              : "-"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Header Text (Editable) */}
                    <EditableSection
                      value={localOverrides.header_text}
                      onChange={(value) => handleFieldChange("header_text", value)}
                      onBlur={() => handleFieldBlur("header_text")}
                      isEditing={editingField === "header_text"}
                      onStartEdit={() => handleStartEdit("header_text")}
                      placeholder="Thank you for the opportunity to provide this proposal..."
                      useGrayBackground={false}
                      accentColor={accentColor}
                      hideLabel={true}
                      isOverridden={localOverrides.header_text !== (effectiveConfig?.header_text || "")}
                      onUndoChanges={() => handleUndoField("header_text")}
                    />

                    {/* Line Items Section */}
                    <div className="mb-8">
                      <div
                        className="font-bold mb-4"
                        style={{
                          color: accentColor,
                          fontSize: '12pt'
                        }}
                      >
                        Scope of Work
                      </div>
                      {lineItems.length === 0 ? (
                        <div className="text-xs text-gray-500 italic text-center py-4 border border-dashed border-gray-300 rounded">
                          No line items found for this project
                        </div>
                      ) : (
                        <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: '10pt' }}>
                          <thead>
                            <tr style={{ backgroundColor: accentColor }}>
                              <th className="text-left text-white py-2.5 px-3 font-semibold" style={{ width: '40%', fontSize: '10pt' }}>Item</th>
                              {displayOptions.show_quantities && (
                                <>
                                  <th className="text-left text-white py-2.5 px-3 font-semibold" style={{ width: '15%', fontSize: '10pt' }}>Quantity</th>
                                  <th className="text-left text-white py-2.5 px-3 font-semibold" style={{ width: '10%', fontSize: '10pt' }}>Unit</th>
                                </>
                              )}
                              <th className="text-left text-white py-2.5 px-3 font-semibold" style={{ width: '15%', fontSize: '10pt' }}>Category</th>
                              {displayOptions.show_line_item_prices && (
                                <th className="text-right text-white py-2.5 px-3 font-semibold" style={{ width: '20%', fontSize: '10pt' }}>Amount</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {lineItems.map((item, index) => (
                              <tr
                                key={item.id || index}
                                style={{ backgroundColor: 'white' }}
                              >
                                <td className="py-2.5 px-3 align-top">
                                  <div className="font-medium" style={{ color: '#1C1C1C' }}>{item.name}</div>
                                  {item.description && (
                                    <div className="mt-1" style={{ color: '#1C1C1C', fontSize: '9pt' }}>{item.description}</div>
                                  )}
                                </td>
                                {displayOptions.show_quantities && (
                                  <>
                                    <td className="py-2.5 px-3" style={{ color: '#1C1C1C' }}>{item.quantity || "-"}</td>
                                    <td className="py-2.5 px-3" style={{ color: '#1C1C1C' }}>{item.unit || "-"}</td>
                                  </>
                                )}
                                <td className="py-2.5 px-3" style={{ color: '#1C1C1C' }}>
                                  {item.category || "-"}
                                </td>
                                {displayOptions.show_line_item_prices && (
                                  <td className="py-2.5 px-3 text-right" style={{ color: '#1C1C1C' }}>
                                    {item.unit_cost ? `$${parseFloat(item.unit_cost).toFixed(2)}` : "-"}
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}

                      {/* Totals */}
                      {displayOptions.show_total && lineItems.length > 0 && (
                        <div className="flex justify-end mt-5">
                          <div style={{ width: '280px' }}>
                            <div
                              className="flex justify-between items-center pt-3"
                              style={{ borderTop: `2px solid ${accentColor}` }}
                            >
                              <span className="text-sm font-bold" style={{ color: accentColor }}>Total</span>
                              <span className="text-sm font-bold" style={{ color: accentColor }}>
                                ${lineItems.reduce((sum, item) => sum + (parseFloat(item.unit_cost) || 0) * (parseFloat(item.quantity) || 1), 0).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Terms & Conditions (Editable) */}
                    <EditableSection
                      label="Terms & Conditions"
                      value={localOverrides.contract_terms}
                      onChange={(value) => handleFieldChange("contract_terms", value)}
                      onBlur={() => handleFieldBlur("contract_terms")}
                      isEditing={editingField === "contract_terms"}
                      onStartEdit={() => handleStartEdit("contract_terms")}
                      placeholder="1. Payment terms: 50% deposit, 50% upon completion&#10;2. Work to begin within 14 days..."
                      multiline
                      accentColor={accentColor}
                      isOverridden={localOverrides.contract_terms !== (effectiveConfig?.contract_terms || "")}
                      onUndoChanges={() => handleUndoField("contract_terms")}
                    />

                    {/* Signature Section */}
                    <div className="mb-8">
                      <div className="flex justify-between" style={{ gap: '40px', marginTop: '40px' }}>
                        <div className="flex-1 pt-4">
                          <div className="border-t border-gray-800 pt-2" style={{ marginTop: '50px' }}>
                            <div style={{ fontSize: '10pt', color: '#1C1C1C' }}>Contractor Signature</div>
                          </div>
                          <div className="border-t border-gray-800 pt-2" style={{ marginTop: '20px' }}>
                            <div style={{ fontSize: '10pt', color: '#1C1C1C' }}>Date</div>
                          </div>
                        </div>
                        <div className="flex-1 pt-4">
                          <div className="border-t border-gray-800 pt-2" style={{ marginTop: '50px' }}>
                            <div style={{ fontSize: '10pt', color: '#1C1C1C' }}>Client Signature</div>
                          </div>
                          <div className="border-t border-gray-800 pt-2" style={{ marginTop: '20px' }}>
                            <div style={{ fontSize: '10pt', color: '#1C1C1C' }}>Date</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Footer Text (Editable) */}
                    <EditableSection
                      value={localOverrides.footer_text}
                      onChange={(value) => handleFieldChange("footer_text", value)}
                      onBlur={() => handleFieldBlur("footer_text")}
                      isEditing={editingField === "footer_text"}
                      onStartEdit={() => handleStartEdit("footer_text")}
                      placeholder="Thank you for your business!"
                      centerText={true}
                      accentColor={accentColor}
                      hideLabel={true}
                      showBorderTop={true}
                      isOverridden={localOverrides.footer_text !== (effectiveConfig?.footer_text || "")}
                      onUndoChanges={() => handleUndoField("footer_text")}
                    />
                    </div>
                  </div>
                )}

                {/* Bottom spacing */}
                <div className="h-6" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

// Editable Section Component
function EditableSection({
  label,
  value,
  onChange,
  onBlur,
  isEditing,
  onStartEdit,
  placeholder,
  multiline = false,
  useGrayBackground = false,
  showBorderTop = false,
  centerText = false,
  accentColor = "#006de2",
  hideLabel = false,
  isOverridden = false,
  onUndoChanges,
}) {
  const inputRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      const el = inputRef.current;
      el.focus();
      // Position cursor at the end of the text
      const length = el.value.length;
      el.setSelectionRange(length, length);
    }
  }, [isEditing]);

  // Custom dashed border using background gradients for more control over dash pattern
  // Dash: 12px, Gap: 8px
  const getDashedBorderStyle = (color) => {
    return {
      backgroundImage: `
        repeating-linear-gradient(to right, ${color} 0, ${color} 12px, transparent 12px, transparent 20px),
        repeating-linear-gradient(to right, ${color} 0, ${color} 12px, transparent 12px, transparent 20px),
        repeating-linear-gradient(to bottom, ${color} 0, ${color} 12px, transparent 12px, transparent 20px),
        repeating-linear-gradient(to bottom, ${color} 0, ${color} 12px, transparent 12px, transparent 20px)
      `,
      backgroundSize: '100% 1px, 100% 1px, 1px 100%, 1px 100%',
      backgroundPosition: '0 0, 0 100%, 0 0, 100% 0',
      backgroundRepeat: 'no-repeat',
    };
  };

  // Get border/background style based on state
  const getContainerStyle = () => {
    // Use custom dashed pattern - same style whether editing or not for seamless experience
    // Just slightly darker when editing to indicate active state
    const dashColor = isEditing ? '#94a3b8' : (isHovered ? '#94a3b8' : '#cbd5e1');
    return {
      ...getDashedBorderStyle(dashColor),
      padding: '12px 16px',
      borderRadius: '8px',
    };
  };

  return (
    <div style={{ marginTop: showBorderTop ? '30px' : '0' }}>
      {/* Top separator line for footer sections */}
      {showBorderTop && (
        <div style={{ borderTop: '1px solid #e0e0e0', marginBottom: '15px' }} />
      )}
      <div
        className={`mb-8 transition-all cursor-pointer group rounded-lg`}
        onClick={() => !isEditing && onStartEdit()}
        style={getContainerStyle()}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Label row with undo option - only shown if label exists and hideLabel is false */}
        {label && !hideLabel ? (
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-bold text-gray-800">{label}</span>
            {isOverridden && onUndoChanges && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onUndoChanges();
                }}
                className="text-xs text-gray-500 hover:text-blue-600 transition-colors"
              >
                Undo all changes
              </button>
            )}
          </div>
        ) : (
          /* For sections without labels, show undo on the right */
          isOverridden && onUndoChanges && (
            <div className="flex justify-end mb-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onUndoChanges();
                }}
                className="text-xs text-gray-500 hover:text-blue-600 transition-colors"
              >
                Undo all changes
              </button>
            </div>
          )
        )}

      <div
        className={`${useGrayBackground ? "rounded-md p-4" : ""} ${centerText ? "text-center" : ""}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          width: '100%',
          flexWrap: 'wrap',
        }}
      >
        {isEditing ? (
          multiline ? (
            <textarea
              ref={inputRef}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onBlur={onBlur}
              className="w-full focus:outline-none resize-none"
              style={{
                border: "none",
                background: "transparent",
                color: value ? "#1C1C1C" : "#9ca3af",
                fontStyle: value ? "normal" : "italic",
                fontSize: centerText ? '9pt' : '10pt',
                lineHeight: "1.6",
                whiteSpace: "pre-wrap",
                padding: 0,
                margin: 0,
                fontFamily: "inherit",
                minHeight: "60px",
              }}
              placeholder={placeholder}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <input
              ref={inputRef}
              type="text"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onBlur={onBlur}
              className="focus:outline-none"
              style={{
                border: "none",
                background: "transparent",
                color: value ? "#1C1C1C" : "#9ca3af",
                fontStyle: value ? "normal" : "italic",
                fontSize: centerText ? '9pt' : '10pt',
                lineHeight: "1.6",
                padding: 0,
                margin: 0,
                fontFamily: "inherit",
                width: "100%",
                textAlign: centerText ? "center" : "left",
              }}
              placeholder={placeholder}
              onClick={(e) => e.stopPropagation()}
            />
          )
        ) : (
          <span
            className={value ? "" : "text-gray-400 italic"}
            style={{
              whiteSpace: "pre-wrap",
              backgroundColor: useGrayBackground ? "#f8f9fa" : "transparent",
              lineHeight: "1.6",
              fontSize: centerText ? '9pt' : '10pt',
              color: value ? '#1C1C1C' : undefined,
              flex: centerText ? 1 : undefined,
              textAlign: centerText ? 'center' : undefined,
            }}
          >
            {value || placeholder}
          </span>
        )}
        {!isEditing && (
          <Pencil
            className="w-3.5 h-3.5 flex-shrink-0"
            style={{ color: '#94a3b8', opacity: 0.5 }}
          />
        )}
      </div>
      </div>
    </div>
  );
}
